import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const OfferCreatePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const caseId = searchParams.get('case_id') || '';
  const clientId = searchParams.get('client_id') || '';

  const [apps, setApps] = useState([]);
  const [form, setForm] = useState({
    application_id: '',
    offer_type: 'conditional',
    issue_date: '',
    expires_at: '',
    tuition_total: '',
    scholarship_amount: '',
    currency: '',
    deposit_amount: '',
    enrollment_deadline: '',
    enrollment_fee: '',
    intake_label: '',
    accepted: false,
    accepted_at: '',
    conditions_json: '',
    additional_conditions: '',
    notes: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    (async () => {
      try {
        const q = supabase.from('uni_applications').select('id, program_id, intake_id, status, metadata').eq('case_id', caseId).order('updated_at', { ascending: false });
        const { data } = await q;
        setApps(data || []);
      } catch { }
    })();
  }, [caseId]);

  const createOffer = async () => {
    try {
      if (!form.application_id) { setToast({ type: 'error', message: 'Select an application' }); return; }
      // Validate JSON
      let conditions = {};
      let additional = {};
      if (form.conditions_json && form.conditions_json.trim()) {
        try { conditions = JSON.parse(form.conditions_json); }
        catch { setToast({ type: 'error', message: 'Conditions must be valid JSON' }); return; }
      }
      if (form.additional_conditions && form.additional_conditions.trim()) {
        try { additional = JSON.parse(form.additional_conditions); }
        catch { setToast({ type: 'error', message: 'Additional conditions must be valid JSON' }); return; }
      }
      setSaving(true);
      const payload = {
        case_id: caseId || null,
        application_id: form.application_id,
        offer_type: form.offer_type,
        issue_date: form.issue_date || null,
        expires_at: form.expires_at || null,
        tuition_total: form.tuition_total ? Number(form.tuition_total) : null,
        accepted: !!form.accepted,
        accepted_at: form.accepted ? (form.accepted_at || new Date().toISOString()) : null,
        metadata: {
          scholarship_amount: form.scholarship_amount ? Number(form.scholarship_amount) : null,
          currency: form.currency || null,
          deposit_amount: form.deposit_amount ? Number(form.deposit_amount) : null,
          enrollment_deadline: form.enrollment_deadline || null,
          enrollment_fee: form.enrollment_fee ? Number(form.enrollment_fee) : null,
          intake_label: form.intake_label || null,
          notes: form.notes || null,
          conditions,
          additional_conditions: additional,
        }
      };
      const { data: inserted, error } = await supabase
        .from('application_offers')
        .insert([payload])
        .select('*')
        .single();
      if (error) throw error;
      const offerId = inserted?.id;
      if (file) {
        const path = `cases/${caseId}/offers/${offerId}/${Date.now()}_${file.name}`;
        const { error: upErr } = await supabase.storage.from('documents').upload(path, file, { upsert: true });
        if (!upErr) {
          const { data: pub } = await supabase.storage.from('documents').getPublicUrl(path);
          await supabase.from('case_notes').insert([{ case_id: caseId, content: `Offer ${offerId} letter: ${pub?.publicUrl || path} (${file.name})` }]).catch(() => { });
        }
      }
      await supabase.from('case_notes').insert([{ case_id: caseId, content: `Offer recorded: ${payload.offer_type} • Tuition ${payload.tuition_total || '-'} ${payload.metadata?.currency || ''}` }]).catch(() => { });
      setToast({ type: 'success', message: 'Offer created' });
      if (caseId) navigate(`/admin/cases/${caseId}`);
      else navigate('/admin');
    } catch (e) {
      setToast({ type: 'error', message: e.message || 'Failed to create offer' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {toast && (
        <div className={`mb-3 p-2 rounded border ${toast.type === 'success' ? 'bg-green-900/20 border-green-800 text-green-400' : 'bg-red-900/20 border-red-800 text-red-400'}`}>{toast.message}</div>
      )}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Record Offer</h2>
          {caseId && (
            <button className="px-3 py-2 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600" onClick={() => navigate(`/admin/cases/${caseId}`)}>Back to Case</button>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Application</label>
            <select className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.application_id} onChange={(e) => setForm({ ...form, application_id: e.target.value })}>
              <option value="">Select application</option>
              {apps.map(a => <option key={a.id} value={a.id}>{a.metadata?.program_text || a.metadata?.university || a.program_id || a.id}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Offer Type</label>
            <select className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.offer_type} onChange={(e) => setForm({ ...form, offer_type: e.target.value })}>
              {['conditional', 'unconditional'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Issue Date</label>
            <input type="date" className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Expires At</label>
            <input type="date" className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Tuition Total</label>
            <input className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.tuition_total} onChange={(e) => setForm({ ...form, tuition_total: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Scholarship Amount</label>
            <input className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.scholarship_amount} onChange={(e) => setForm({ ...form, scholarship_amount: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Currency</label>
            <input className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Deposit Amount</label>
            <input className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Enrollment Deadline</label>
            <input type="date" className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.enrollment_deadline} onChange={(e) => setForm({ ...form, enrollment_deadline: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Enrollment Fee</label>
            <input className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={form.enrollment_fee} onChange={(e) => setForm({ ...form, enrollment_fee: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Intake (Label)</label>
            <input className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g., Fall 2025" value={form.intake_label} onChange={(e) => setForm({ ...form, intake_label: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-400 mb-1">Conditions (JSON)</label>
            <textarea rows={3} className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder={'{ "condition": "..." }'} value={form.conditions_json} onChange={(e) => setForm({ ...form, conditions_json: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-400 mb-1">Additional Conditions (JSON)</label>
            <textarea rows={3} className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder={'{ "condition": "..." }'} value={form.additional_conditions} onChange={(e) => setForm({ ...form, additional_conditions: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-400 mb-1">Notes</label>
            <textarea rows={3} className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder={'Internal notes for follow-up'} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-400 mb-1">Offer Letter</label>
            <input type="file" className="text-gray-400" onChange={(e) => setFile((e.target.files || [])[0] || null)} />
          </div>
          <div className="md:col-span-2">
            <label className="inline-flex items-center gap-2 text-xs text-gray-300">
              <input type="checkbox" checked={form.accepted} onChange={(e) => setForm({ ...form, accepted: e.target.checked })} className="rounded border-gray-600 bg-gray-700 text-blue-500" />
              Mark as accepted
            </label>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button className="px-3 py-2 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600" onClick={() => caseId ? navigate(`/admin/cases/${caseId}`) : navigate('/admin')}>Cancel</button>
          <button className="px-3 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" disabled={saving} onClick={createOffer}>{saving ? 'Saving…' : 'Create Offer'}</button>
        </div>
      </div>
    </div>
  );
};

export default OfferCreatePage;
